import { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform, useWindowDimensions, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import {
  matchCardHeroShellLayoutStyles as layout,
  MATCH_CARD_HERO_WAVE_VIEWBOX,
} from './MatchCardHeroShell.styles';

/**
 * Purple band + white mask. Path uses horizontal bleed in viewBox so left/right endpoints
 * align with the physical card edges (avoids purple “shoulders”).
 */
export function MatchCardHeroShell() {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const isDark = themeMode === 'dark';
  const { width: screenW } = useWindowDimensions();

  const { minX, w: vbW, h: vbH } = MATCH_CARD_HERO_WAVE_VIEWBOX;
  const guessCardW = Math.min(screenW * 0.94, Platform.OS === 'web' ? 520 : screenW);
  const [bandPxW, setBandPxW] = useState(guessCardW);

  const onBandLayout = useCallback((e: LayoutChangeEvent) => {
    const nw = e.nativeEvent.layout.width;
    if (nw > 0) setBandPxW(nw);
  }, []);

  const wavePxH = (bandPxW * vbH) / vbW;

  const { gradientColors, locations } = useMemo(() => {
    if (isDark) {
      return {
        gradientColors: [
          theme.background.primary,
          theme.primaryDark,
          theme.background.card,
        ] as [string, string, string],
        locations: [0, 0.38, 1] as [number, number, number],
      };
    }
    return {
      gradientColors: [theme.primaryDark, theme.primary] as [string, string],
      locations: [0, 1] as [number, number],
    };
  }, [isDark, theme.background.card, theme.background.primary, theme.primary, theme.primaryDark]);

  const wavePath = useMemo(() => {
    const yBase = 86;
    const yPeak = 6;
    const xLeft = minX;
    const xRight = minX + vbW;
    const span = vbW;
    const c1x = minX + span / 3;
    const c2x = minX + (span * 2) / 3;
    return `M${xLeft},${yBase} C${c1x},${yPeak} ${c2x},${yPeak} ${xRight},${yBase} L${xRight},${vbH} L${xLeft},${vbH} Z`;
  }, [minX, vbH, vbW]);

  return (
    <View style={layout.bgContainer} onLayout={onBandLayout}>
      <LinearGradient
        colors={gradientColors}
        locations={locations}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={layout.gradientBand}
      />
      {/* Inline height is computed from onLayout (measure-based) — treated like an
          Animated value: it changes with the rendered card width every layout pass,
          so it cannot live in StyleSheet. */}
      <View style={[layout.waveLayer, { height: wavePxH }]} pointerEvents="none">
        <Svg
          width="100%"
          height={wavePxH}
          viewBox={`${minX} 0 ${vbW} ${vbH}`}
          preserveAspectRatio="none"
        >
          <Path d={wavePath} fill={theme.background.card} />
        </Svg>
      </View>
    </View>
  );
}
